import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';
import { Box } from '@mui/material';
import { Chip, Tooltip } from '@biq/apex-ui';
import { themeObject, toLocalDateTimeString } from 'lib';
import { EditorView } from '@uiw/react-codemirror';

export const tableHeight: number = 260;

export const PIPELINES = 'Pipelines';
export const STEPS = 'Steps';

export type TabsItemsType = typeof PIPELINES | typeof STEPS;

export type ColumnDef = {
    field: string;
    headerName: string;
    width?: number;
    editable?: boolean;
    flex?: number;
    headerClassName?: string;
    resizable?: boolean;
    renderCell?: (params: any) => React.JSX.Element;
};

export const TabItems: TabItemsType[] = [
    { label: PIPELINES },
    { label: STEPS },
];

export const pipelinesAndStepsStyles = {
    tabsStyles: {
        minHeight: '32px',
        '& .MuiTabs-scroller': {
            '& .MuiTabs-flexContainer': {
                gap: '12px',
            },
        },
    },
    tabItemStyles: {
        minHeight: '0',
        m: '0px !important',
        p: '2px 20px',
    },
    ellipsifyPipelinesDetails: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    tooltipOverflow: {
        maxHeight: '50vh',
        overflowY: 'auto',
    },
    menuStyles: {
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: -7,
        },
    },
    emptyStateBodyMessageStyles: {
        width: '638px',
        height: '105px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'start',
    },
    emptyImageStyles: {
        height: '112.42px',
        width: '138.86px',
    },
    emptyHeadingContainerStyles: {
        mt: 2,
    },
};

export const commonStyles = {
    tabsContainerStyles: {
        mt: 0,
        '.MuiTabs-root': {
            '& .MuiTabs-flexContainer': {
                flexDirection: 'column',
            },
        },
    },
    tabsStyles: {
        minHeight: '32px',
        width: '160px',
        overflow: 'visible',
        '& .MuiTabs-scroller': {
            overflow: 'visible !important',
            '& .MuiTabs-flexContainer': {
                gap: '12px',
                '& .MuiTab-root': {
                    width: '100%',
                    overflow: 'visible',
                },
                '& img': {
                    top: '-10px !important',
                    right: '10px !important',
                },
            },
        },
    },
    tabItemStyles: {
        minHeight: '0',
        m: '0px !important',
        p: '2px 20px',
    },
    deleteIconStyles: {
        color: themeObject.palette.tertiary.red[300],
    },
    editIconStyles: {
        color: themeObject.palette.primary[400],
    },
    addButtonStyles: {
        borderRadius: '64px',
        height: '40px',
        '& .MuiTypography-root': {
            fontFamily: 'Red Hat Text',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '24px',
            color: themeObject.palette.grayScale[700],
            ml: '2px',
        },
    },
    headerTabButtonStyles: {
        padding: '5px 16px 5px 12px',
        gap: '3px',
    },
    trialRunButtonIcon: {
        color: themeObject.palette.tertiary.purple[400],
        height: '24px',
        width: '24px',
    },
    codeMirrorConatiner: {
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        borderRadius: '4px',
        boxShadow: 'none',
    },
    codeMirrorError: {
        border: `1px solid ${themeObject.palette.tertiary.red[300]}`,
    },
};

export const PipelinesCodeMirrorStyles = EditorView.baseTheme({
    '&.cm-editor': {
        borderRadius: '4px',
    },
    '.cm-scroller': {
        borderRadius: '4px',
    },
});

const renderEllipsifiedTooltipCell = (params: any): React.JSX.Element => {
    if (!params.value) return <></>;

    return (
        <Tooltip
            title={
                <Box sx={{ ...pipelinesAndStepsStyles.tooltipOverflow }}>
                    {params.value}
                </Box>
            }
            arrow
        >
            <Box sx={{ ...pipelinesAndStepsStyles.ellipsifyPipelinesDetails }}>
                {params.value}
            </Box>
        </Tooltip>
    );
};

export const getPipelinesColumns = (): any[] => {
    return [
        {
            field: 'name',
            headerName: 'Pipeline Name',
            width: 260,
            editable: false,
            flex: 1,
            resizable: false,
            renderCell: renderEllipsifiedTooltipCell,
        },
        {
            field: 'description',
            headerName: 'Description',
            editable: false,
            flex: 2,
            resizable: false,
            renderCell: renderEllipsifiedTooltipCell,
        },
        {
            field: 'timestamp',
            headerName: 'Last Modified',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: (params: any): any => {
                return toLocalDateTimeString(params.value, true);
            },
        },
    ];
};

export const getStepsColumns = (): any[] => {
    return [
        {
            field: 'name',
            headerName: 'Step Name',
            width: 260,
            flex: 1,
            editable: false,
            renderCell: renderEllipsifiedTooltipCell,
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 170,
            editable: false,
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 250,
            flex: 1,
            editable: false,
            renderCell: renderEllipsifiedTooltipCell,
        },
        {
            field: 'timestamp',
            headerName: 'Last Modified',
            width: 170,
            renderCell: (params: any): any => {
                return toLocalDateTimeString(params.value, true);
            },
        },
        {
            field: 'inPipelines',
            headerName: 'In Pipelines (#)',
            width: 170,
            editable: false,
            align: 'center',
            renderCell: (params: any): React.JSX.Element => {
                return (
                    <Chip
                        label={params.value}
                        color={params.value === 0 ? 'error' : 'primary'}
                        variant={params.value === 0 ? 'filled' : 'outlined'}
                        size='small'
                        readOnly
                    />
                );
            },
        },
    ];
};

export const getColumns = (selectedTab: TabsItemsType): ColumnDef[] => {
    const columns = {
        [PIPELINES]: getPipelinesColumns(),
        [STEPS]: getStepsColumns(),
    };

    return columns[selectedTab];
};

export interface StepTypeOption {
    label: string;
    value: string;
    type: string;
}

export const FIELD_MAPPING = 'Field Mapping';
export const JAVASCRIPT_TRANSFORM = 'JavaScript Transform';
export const RULE_SET = 'Rule Set';

export type StepTypeLabel =
    | typeof FIELD_MAPPING
    | typeof JAVASCRIPT_TRANSFORM
    | typeof RULE_SET;

export const StepsType: StepTypeOption[] = [
    {
        label: JAVASCRIPT_TRANSFORM,
        value: 'JS_TRANSFORM',
        type: 'transform',
    },
    {
        label: RULE_SET,
        value: 'RULE_SET',
        type: 'ruleset',
    },
    {
        label: FIELD_MAPPING,
        value: 'FIELD_MAPPING',
        type: 'fieldmapping',
    },
];

export const StepsTypeMap: Record<string, string> = StepsType.reduce(
    (acc, curr) => {
        acc[curr.value] = curr.label;
        return acc;
    },
    {} as Record<string, string>
);
